import React from 'react'
import styled from 'styled-components'
import {
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningMetricSources,
} from '@popety-io/types'
import { formatMetricValue, getSourceColor } from '../utils/zoningHelpers'
import { MinMaxValue } from './MinMaxLabel'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
  AuthorizedValue,
  AuthorizedValueWithBadge,
} from './ZoneMetricsTable.styles'
import { EditableMinMaxInput } from './EditableMinMaxInput'
import { DualSourceBadgeLine } from './DualSourceBadgeLine'
import { VisibilityToggle } from './VisibilityToggle'

const SubNote = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

const RowWithOptionalBorder = styled.tr<{
  $showBorder: boolean
  $hidden?: boolean
}>`
  td {
    height: ${({ theme }) => theme.spacing.xlarge};
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.medium}`};
    border-bottom: ${({ $showBorder, theme }) =>
      $showBorder ? `1px solid ${theme.colors.border.light}` : 'none'};
    opacity: ${({ $hidden }) => ($hidden ? 0.45 : 1)};
  }
`

const LabelWithToggle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
`

export interface StandardMetricRowProps {
  metricKey: string
  metricLabel: string
  metricDescription?: string
  isEditing: boolean

  // Values
  currentValue?: number
  minValue?: number
  maxValue?: number

  // Sources (legacy single-bundle — used for single-value metrics)
  minSource?: string
  maxSource?: string
  minReference?: any
  maxReference?: any
  defaultSource?: zoning_indice_sources
  agreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  minDefaultSource?: zoning_indice_sources
  maxDefaultSource?: zoning_indice_sources
  // Sources (per-bound — used for range metrics when bounds may differ)
  minSources?: ZoningMetricSources
  maxSources?: ZoningMetricSources
  minAgreement?: zoning_sources_agreement
  maxAgreement?: zoning_sources_agreement

  // State
  isOverridden: boolean
  isPending: boolean
  isMinOverridden?: boolean
  isMinPending?: boolean
  unit?: string

  // Display
  showBorder?: boolean // Control border visibility
  /** Small note under the label (e.g. "input of the standard construction rules") */
  subNote?: string
  // Visibility (edit mode)
  showVisibilityToggle?: boolean
  isRowHidden?: boolean
  onToggleVisibility?: () => void
  hideTooltip?: string
  showTooltip?: string

  // Handlers
  onValueChange: (metricKey: string, value: number) => void
  onValueClear: (metricKey: string) => void
  onAuthorizedClick: (metricKey: string) => void
}

/**
 * Standard row component for regular metrics (heights, floors, other)
 * No radio selector, just displays values with edit capability
 */
export const StandardMetricRow: React.FC<StandardMetricRowProps> = ({
  metricKey,
  metricLabel,
  // metricDescription,
  isEditing,
  currentValue,
  minValue,
  maxValue,
  // minSource,
  maxSource,
  // minReference,
  maxReference,
  defaultSource,
  agreement,
  sources,
  minDefaultSource,
  maxDefaultSource,
  minSources,
  maxSources,
  minAgreement,
  maxAgreement,
  isOverridden,
  isPending,
  isMinOverridden = false,
  isMinPending = false,
  unit,
  showBorder = false,
  subNote,
  showVisibilityToggle = false,
  isRowHidden = false,
  onToggleVisibility,
  hideTooltip,
  showTooltip,
  onValueChange,
  onValueClear,
  onAuthorizedClick,
}) => {
  // Calculate difference
  const calculateDifference = (): {
    value: number
    isPositive: boolean
  } | null => {
    if (maxValue === undefined || currentValue === undefined) return null
    const diff = maxValue - currentValue
    return { value: diff, isPositive: diff >= 0 }
  }

  const diff = calculateDifference()

  // Format authorized value with min/max labels
  const hasMinMax = minValue !== undefined && maxValue !== undefined
  const hasMax = maxValue !== undefined
  const hasMin = minValue !== undefined

  const authorizedContent = hasMinMax ? (
    <MinMaxValue
      minDisplay={formatMetricValue(minValue!, unit, metricKey)}
      maxDisplay={formatMetricValue(maxValue!, unit, metricKey)}
    />
  ) : hasMax ? (
    <MinMaxValue maxDisplay={formatMetricValue(maxValue!, unit, metricKey)} />
  ) : hasMin ? (
    <MinMaxValue minDisplay={formatMetricValue(minValue!, unit, metricKey)} />
  ) : defaultSource ? (
    <>N/A</>
  ) : null

  const isAnyOverridden = isOverridden || isMinOverridden
  const isAnyPending = isPending || isMinPending
  const sourceColor = getSourceColor(maxSource, isAnyOverridden)

  return (
    <RowWithOptionalBorder $showBorder={showBorder} $hidden={isRowHidden}>
      <td>
        <LabelWithToggle>
          {showVisibilityToggle && onToggleVisibility && (
            <VisibilityToggle
              isHidden={isRowHidden}
              onToggle={onToggleVisibility}
              hideTooltip={hideTooltip}
              showTooltip={showTooltip}
            />
          )}
          <div>
            <MetricLabel>{metricLabel}</MetricLabel>
            {subNote && <SubNote>{subNote}</SubNote>}
          </div>
        </LabelWithToggle>
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, metricKey)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {isEditing && !isRowHidden ? (
          <EditableMinMaxInput
            metricKey={metricKey}
            minValue={minValue}
            maxValue={maxValue}
            isMinOverridden={isMinOverridden}
            isMaxOverridden={isOverridden}
            isMinPending={isMinPending}
            isMaxPending={isPending}
            minSource={minDefaultSource}
            maxSource={maxDefaultSource}
            onValueChange={onValueChange}
            onValueClear={onValueClear}
          />
        ) : authorizedContent || maxReference || defaultSource ? (
          <AuthorizedValueWithBadge>
            <AuthorizedValue
              $source={maxSource}
              $isUserEdited={isAnyOverridden}
              $isPending={isAnyPending}
              onClick={() => onAuthorizedClick(metricKey)}
              style={{ cursor: 'pointer', color: sourceColor }}
              title="Click to view source details"
            >
              {authorizedContent || 'N/A'}
            </AuthorizedValue>
            <DualSourceBadgeLine
              metricKey={metricKey}
              isOverridden={isAnyOverridden}
              defaultSource={defaultSource}
              agreement={agreement}
              sources={sources}
              minDefaultSource={minDefaultSource}
              maxDefaultSource={maxDefaultSource}
              minAgreement={minAgreement}
              maxAgreement={maxAgreement}
              minSources={minSources}
              maxSources={maxSources}
              hasMinMax={hasMinMax}
            />
          </AuthorizedValueWithBadge>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={diff?.isPositive ?? true}>
        {diff ? (
          <>
            {diff.isPositive && '+'}
            {formatMetricValue(diff.value, unit, metricKey)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </RowWithOptionalBorder>
  )
}
